import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { login, register, checkEmail } from '@/api/query/auth';
import api from '@/api/api';
import storageManager from '@/api/storageManager';
import { checkRegistrationPassword } from '@/common/functions/checkPassword';
import { getRegistrationUtms } from '@/common/functions/getUtms';
import {
	AUTH_COMPLETE_DELAY_MS,
	AUTH_STEPS,
	AUTH_SUBMIT_STATE,
	AUTH_SUCCESS_DELAY_MS,
} from '../constants/authForm.constants';

export const useAuthForm = ({
	mode,
	onSuccess,
	onStepChange,
	translations,
}) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState(null);
	const [currentStep, setCurrentStep] = useState(AUTH_STEPS.EMAIL);
	const [authSubmitState, setAuthSubmitState] = useState(
		AUTH_SUBMIT_STATE.IDLE,
	);

	const successTimeoutRef = useRef(null);
	const completeTimeoutRef = useRef(null);
	const autoAdvanceEmailRef = useRef(false);

	const { lang } = useContext(LanguageContext);
	const {
		authRegistrationSource,
		authReturnPath,
		authPrefilledEmail,
		authAutoAdvanceEmail,
		refreshAuthState,
	} = useGuestAuth();

	const updateStep = (step) => {
		setCurrentStep(step);
		onStepChange?.(step);
	};

	useEffect(() => {
		return () => {
			clearTimeout(successTimeoutRef.current);
			clearTimeout(completeTimeoutRef.current);
		};
	}, []);

	const clearAuthSuccessTimers = () => {
		clearTimeout(successTimeoutRef.current);
		clearTimeout(completeTimeoutRef.current);
	};

	const scheduleAuthSuccess = (isRegistration) => {
		setAuthSubmitState(AUTH_SUBMIT_STATE.PENDING);

		successTimeoutRef.current = setTimeout(() => {
			setAuthSubmitState(AUTH_SUBMIT_STATE.SUCCESS);

			completeTimeoutRef.current = setTimeout(() => {
				onSuccess?.({ isRegistration });
			}, AUTH_COMPLETE_DELAY_MS);
		}, AUTH_SUCCESS_DELAY_MS);
	};

	const persistAuthSession = ({ data }) => {
		storageManager.auth.setToken(data.token, api.axios);
		storageManager.auth.setRefreshToken(data.refreshToken);
		void refreshAuthState();
	};

	const handleAuthSuccess = ({ data }) => {
		persistAuthSession({ data });
		scheduleAuthSuccess(false);
	};

	const loginMutation = useMutation({
		mutationFn: (payload) => login(payload),
		onSuccess: handleAuthSuccess,
		onError: () => {
			clearAuthSuccessTimers();
			setAuthSubmitState(AUTH_SUBMIT_STATE.IDLE);
			setError(
				translations.wrongPasswordMessage ||
					'Incorrect password. Please try again.',
			);
		},
	});

	const registerMutation = useMutation({
		mutationFn: (payload) => register(payload),
		onSuccess: ({ data }) => {
			persistAuthSession({ data });
			scheduleAuthSuccess(data.is_new_customer === true);
		},
		onError: (res) => {
			clearAuthSuccessTimers();
			setAuthSubmitState(AUTH_SUBMIT_STATE.IDLE);

			if (res.message?.includes('Account already exists')) {
				setPassword('');
				setConfirmPassword('');
				updateStep(AUTH_STEPS.PASSWORD_LOGIN);
				setError(
					translations.accountExistsLogin ||
						'This account already exists. Please log in.',
				);
				return;
			}

			setError(res.message);
		},
	});

	const checkEmailMutation = useMutation({
		mutationFn: (emailPayload) => checkEmail(emailPayload),
		onSuccess: ({ data }) => {
			setError(null);

			if (data.message === AUTH_STEPS.PASSWORD_LOGIN) {
				updateStep(AUTH_STEPS.PASSWORD_LOGIN);
				return;
			}

			if (data.message === AUTH_STEPS.PASSWORD_SET) {
				updateStep(AUTH_STEPS.PASSWORD_SET);
				return;
			}

			updateStep(AUTH_STEPS.PASSWORD_REGISTER);
		},
		onError: (res) => setError(res.message),
	});

	useEffect(() => {
		const emailParam =
			authPrefilledEmail ||
			new URLSearchParams(window.location.search).get('email');

		if (!emailParam) {
			return;
		}

		setEmail(emailParam);

		if (!authAutoAdvanceEmail || autoAdvanceEmailRef.current) {
			return;
		}

		autoAdvanceEmailRef.current = true;
		checkEmailMutation.mutate({
			email: emailParam.toLowerCase().replace(/\s/g, ''),
		});
	}, [authAutoAdvanceEmail, authPrefilledEmail, checkEmailMutation]);

	const normalizedEmail = email?.toLowerCase()?.replace(/\s/g, '');

	const handleSubmit = (event) => {
		event.preventDefault();
		setError(null);

		if (currentStep === AUTH_STEPS.EMAIL) {
			checkEmailMutation.mutate({ email: normalizedEmail });
			return;
		}

		if (currentStep === AUTH_STEPS.PASSWORD_LOGIN) {
			loginMutation.mutate({ email: normalizedEmail, password });
			return;
		}

		if (
			currentStep === AUTH_STEPS.PASSWORD_REGISTER ||
			currentStep === AUTH_STEPS.PASSWORD_SET
		) {
			const passwordValidation = checkRegistrationPassword(
				password,
				confirmPassword,
			);

			if (!passwordValidation.valid) {
				setError(
					translations[passwordValidation.messageKey] ||
						passwordValidation.messageKey,
				);
				return;
			}

			registerMutation.mutate({
				email: normalizedEmail,
				password,
				lang,
				gainUtms: getRegistrationUtms(authRegistrationSource, authReturnPath),
			});
		}
	};

	const handleGoBack = () => {
		updateStep(AUTH_STEPS.EMAIL);
		setPassword('');
		setConfirmPassword('');
		setError(null);
	};

	const isCheckEmailLoading = checkEmailMutation.isLoading;
	const isAuthMutationLoading =
		loginMutation.isLoading || registerMutation.isLoading;
	const isFinalAuthStep =
		currentStep === AUTH_STEPS.PASSWORD_LOGIN ||
		currentStep === AUTH_STEPS.PASSWORD_REGISTER ||
		currentStep === AUTH_STEPS.PASSWORD_SET;
	const isAuthPending =
		isFinalAuthStep &&
		(isAuthMutationLoading || authSubmitState === AUTH_SUBMIT_STATE.PENDING);
	const isAuthSuccess =
		isFinalAuthStep && authSubmitState === AUTH_SUBMIT_STATE.SUCCESS;
	const isSubmitDisabled =
		isCheckEmailLoading || isAuthPending || isAuthSuccess;

	return {
		email,
		setEmail,
		password,
		setPassword,
		confirmPassword,
		setConfirmPassword,
		error,
		currentStep,
		normalizedEmail,
		handleSubmit,
		handleGoBack,
		isCheckEmailLoading,
		isAuthPending,
		isAuthSuccess,
		isSubmitDisabled,
	};
};
